<template>
  <div class="flex h-full min-h-0 flex-col">
    <!-- Bulk actions: pinned top strip (outside scroll) so it never covers tasks -->
    <div
      v-if="organizeMode && selectedIds.size > 0"
      class="flex flex-shrink-0 flex-wrap items-center gap-[8px] border-b border-teal-200 bg-teal-50 px-[12px] py-[8px] dark:border-teal-800 dark:bg-teal-950/50 sm:px-[16px] md:px-[20px]"
      role="toolbar"
      :aria-label="`Bulk actions for ${selectedIds.size} selected tasks`"
    >
      <span class="text-xs font-medium tabular-nums text-teal-800 dark:text-teal-200">
        {{ selectedIds.size }}
      </span>

      <div class="flex flex-wrap items-center gap-[3px]">
        <button
          v-for="s in sections"
          :key="s.id"
          @click="bulkMoveToSection(s.id)"
          class="section-chip flex h-[20px] min-w-[22px] items-center justify-center rounded px-[5px] text-[10px] font-semibold uppercase leading-none bg-white text-gray-600 ring-1 ring-teal-200 hover:bg-teal-500 hover:text-white dark:bg-gray-50 dark:ring-teal-800"
          :title="`Move to ${s.name}`"
        >
          {{ sectionAbbrev(s) }}
        </button>
      </div>

      <select
        v-model="bulkProjectId"
        @change="applyBulkProject"
        class="max-w-[160px] rounded border border-teal-300 bg-white px-[8px] py-[3px] text-xs focus:border-teal-500 focus:outline-none dark:border-teal-700 dark:bg-gray-50"
        title="Change project"
      >
        <option value="" disabled>Project…</option>
        <option value="__none__">No project</option>
        <option v-for="p in sortedProjects" :key="p.id" :value="String(p.id)">{{ p.display_label || p.name }}</option>
      </select>

      <button
        type="button"
        @click="applyBulkComplete"
        class="flex h-[24px] items-center gap-[4px] rounded px-[6px] text-xs font-medium text-green-700 ring-1 ring-green-300 hover:bg-green-50 dark:text-green-400 dark:ring-green-800 dark:hover:bg-green-950/40"
        :title="`Mark ${selectedIds.size} task${selectedIds.size === 1 ? '' : 's'} done`"
      >
        <svg class="h-[14px] w-[14px]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
        </svg>
        Done
      </button>

      <button
        type="button"
        @click="applyBulkDelete"
        class="flex h-[24px] w-[24px] items-center justify-center rounded text-red-400 ring-1 ring-red-200 hover:bg-red-50 hover:text-red-600 dark:ring-red-800 dark:hover:bg-red-950/40"
        :title="`Delete ${selectedIds.size} task${selectedIds.size === 1 ? '' : 's'}`"
      >
        <svg class="h-[14px] w-[14px]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
        </svg>
      </button>

      <button
        @click="clearSelection"
        class="ml-auto p-1 rounded text-teal-700 hover:bg-teal-100 dark:text-teal-300 dark:hover:bg-teal-900/40"
        title="Clear selection"
      >
        <svg class="h-[14px] w-[14px]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </div>

    <!-- Scrollable task list -->
    <div class="min-h-0 flex-1 overflow-y-auto px-[8px] py-[6px] sm:px-[12px] md:px-[20px] md:py-[8px]">
      <!-- Project header when filtering -->
      <div v-if="activeProject" class="mb-[12px] flex items-center gap-[12px] border-b border-gray-200 pb-[10px] dark:border-gray-300">
        <span v-if="activeProject.color" class="h-[12px] w-[12px] flex-shrink-0 rounded-full" :style="{ background: activeProject.color }"></span>
        <h2 class="text-lg font-semibold text-gray-700 dark:text-gray-300">{{ activeProject.display_label || activeProject.name }}</h2>
        <span class="text-xs text-gray-400">{{ activeProject.task_count }} tasks</span>
      </div>

      <div class="w-full" :class="organizeMode ? 'max-w-3xl' : 'max-w-2xl'">
        <SectionGroup
          v-for="section in sections"
          :key="section.id"
          :section="section"
          :tasks="filteredTasks(section)"
          :projects="projects"
          :all-sections="sections"
          :selected-ids="selectedIds"
          :organize-mode="organizeMode"
          @select-task="$emit('select-task', $event)"
          @create-task="$emit('create-task', { ...$event, section_id: section.id, ...(filterProjectId ? { project_id: filterProjectId } : {}) })"
          @toggle-complete="$emit('toggle-complete', $event)"
          @toggle-collapse="$emit('toggle-collapse', section.id)"
          @reorder="$emit('reorder', $event)"
          @update-task="(id, attrs) => $emit('update-task', id, attrs)"
          @delete-task="(id) => $emit('delete-task', id)"
          @move-section="(id, sectionId) => $emit('move-section', id, sectionId)"
          @change-project="(id, projectId) => $emit('change-project', id, projectId)"
          @rename-section="(id, name) => $emit('rename-section', id, name)"
          @move-section-up="(id) => $emit('move-section-up', id)"
          @move-section-down="(id) => $emit('move-section-down', id)"
          @delete-section="(id) => $emit('delete-section', id)"
          @toggle-select="toggleSelect"
          @range-select="rangeSelect"
        />

        <div v-if="!organizeMode" class="mt-[6px] md:mt-[8px]">
          <button v-if="!showNewSection" @click="showNewSection = true"
            class="flex items-center gap-[4px] text-xs text-gray-400 hover:text-gray-600">
            <svg class="h-[12px] w-[12px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            Add section
          </button>
          <div v-else class="flex items-center gap-[8px]">
            <input ref="newSectionInput" v-model="newSectionName"
              @keydown.enter.prevent="createSection" @keydown.escape="showNewSection = false" @blur="showNewSection = false"
              placeholder="Section name..."
              class="w-[160px] rounded border border-gray-300 bg-white px-[8px] py-[4px] text-sm focus:border-teal-500 focus:outline-none" />
          </div>
        </div>

        <div v-if="sections.length === 0" class="py-[64px] text-center text-gray-400">
          <p class="mb-[8px] text-lg">No tasks yet</p>
          <p class="text-sm">Create a task in any section to get started</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, watch, onMounted, onUnmounted } from 'vue'
import SectionGroup from './SectionGroup.vue'

const props = defineProps({
  sections: Array,
  tasksStore: Object,
  projects: Array,
  filterProjectId: { type: [Number, null], default: null },
  organizeMode: { type: Boolean, default: false },
})

const emit = defineEmits([
  'select-task', 'create-task', 'toggle-complete', 'toggle-collapse',
  'reorder', 'update-task', 'delete-task', 'move-section', 'change-project',
  'rename-section', 'move-section-up', 'move-section-down', 'delete-section', 'create-section',
  'bulk-move-section', 'bulk-change-project', 'bulk-complete', 'bulk-delete', 'exit-organize',
])

const showNewSection = ref(false)
const newSectionName = ref('')
const newSectionInput = ref(null)

const selectedIds = ref(new Set())
const lastSelectedId = ref(null)
const bulkProjectId = ref('')

const SLUG_ABBREV = {
  masterlist: 'M',
  today: 'T',
  next: 'N',
  someday: 'S',
  archive: 'A',
}

function sectionAbbrev(s) {
  if (s.slug && SLUG_ABBREV[s.slug]) return SLUG_ABBREV[s.slug]
  const name = (s.name || '?').trim()
  return name.charAt(0).toUpperCase()
}

watch(showNewSection, (v) => {
  if (v) nextTick(() => newSectionInput.value?.focus())
})

// Clear selection whenever organize mode turns off
watch(() => props.organizeMode, (on) => {
  if (!on) clearSelection()
})

const activeProject = computed(() => {
  if (!props.filterProjectId) return null
  return props.projects.find(p => p.id === props.filterProjectId)
})

const sortedProjects = computed(() =>
  [...(props.projects || [])].sort((a, b) =>
    (a.display_label || a.name).localeCompare(b.display_label || b.name)
  )
)

const visibleTaskIds = computed(() => {
  const ids = []
  for (const section of props.sections) {
    if (section.is_collapsed) continue
    for (const t of filteredTasks(section)) ids.push(t.id)
  }
  return ids
})

function filteredTasks(section) {
  const sectionTasks = props.tasksStore.tasksBySection(section.id).value
  if (!props.filterProjectId) return sectionTasks
  return sectionTasks.filter(t => t.project_id === props.filterProjectId)
}

function toggleSelect(id) {
  if (!props.organizeMode) return
  const next = new Set(selectedIds.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  selectedIds.value = next
  lastSelectedId.value = id
}

function rangeSelect(id) {
  if (!props.organizeMode) return
  const ids = visibleTaskIds.value
  const end = ids.indexOf(id)
  if (end === -1) {
    toggleSelect(id)
    return
  }
  const start = lastSelectedId.value != null ? ids.indexOf(lastSelectedId.value) : end
  const from = Math.min(start === -1 ? end : start, end)
  const to = Math.max(start === -1 ? end : start, end)
  const next = new Set(selectedIds.value)
  for (let i = from; i <= to; i++) next.add(ids[i])
  selectedIds.value = next
  lastSelectedId.value = id
}

function clearSelection() {
  selectedIds.value = new Set()
  lastSelectedId.value = null
  bulkProjectId.value = ''
}

function bulkMoveToSection(sectionId) {
  if (selectedIds.value.size === 0) return
  emit('bulk-move-section', [...selectedIds.value], sectionId)
  clearSelection()
}

function applyBulkProject() {
  if (!bulkProjectId.value || selectedIds.value.size === 0) return
  const projectId = bulkProjectId.value === '__none__' ? null : Number(bulkProjectId.value)
  emit('bulk-change-project', [...selectedIds.value], projectId)
  clearSelection()
}

function applyBulkComplete() {
  if (selectedIds.value.size === 0) return
  emit('bulk-complete', [...selectedIds.value])
  clearSelection()
}

function applyBulkDelete() {
  const count = selectedIds.value.size
  if (count === 0) return
  const label = count === 1 ? 'this task' : `these ${count} tasks`
  if (!confirm(`Delete ${label}? This cannot be undone.`)) return
  emit('bulk-delete', [...selectedIds.value])
  clearSelection()
}

function createSection() {
  const name = newSectionName.value.trim()
  if (name) {
    emit('create-section', name)
    newSectionName.value = ''
    showNewSection.value = false
  }
}

function onKeydown(e) {
  if (e.key !== 'Escape') return
  if (selectedIds.value.size > 0) {
    clearSelection()
    e.stopPropagation()
  } else if (props.organizeMode) {
    emit('exit-organize')
    e.stopPropagation()
  }
}

onMounted(() => window.addEventListener('keydown', onKeydown, true))
onUnmounted(() => window.removeEventListener('keydown', onKeydown, true))
</script>
